import { useCallback, useEffect, useRef, useState } from 'react';
import { CompanyRequestError } from '@/company/api';
import { useCompany } from '@/company/context';
import { agentErrorMessage } from './format';
import connectionsCopy from '@/i18n/ns/connections';
import { useNamespaceCopy } from '@/i18n/ns';

const isOff = (error: unknown) =>
  error instanceof CompanyRequestError && error.code === 'agent_access_disabled';

/**
 * Company-and-person scoped server state. A company or user switch discards
 * the previous snapshot immediately, and a response that belongs to an older
 * scope (or an unmounted view) is dropped rather than shown. Within one scope,
 * reads are applied in the order they were sent: a poll that left before a
 * change and answers after the change's re-read never restores the old list.
 * Nothing is read until the company and person are known.
 */
export function useScopedResource<T>(load: () => Promise<T>, failureMessage: string) {
  const copy = useNamespaceCopy(connectionsCopy);
  const company = useCompany();
  const companyId = company.data?.company?.id ?? null;
  const userId = company.data?.membership?.userId ?? null;
  const known = Boolean(companyId && userId);
  const scope = JSON.stringify([companyId, userId]);
  const [snapshot, setSnapshot] = useState<{ scope: string; value: T } | null>(null);
  const [failure, setFailure] = useState<{ scope: string; message: string } | null>(null);
  // The server has this feature switched off. That is a state, not a failure: nothing to retry,
  // and nothing read before it may be shown after it.
  const [off, setOff] = useState<{ scope: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  // Every read in this generation gets the next number; only a read newer
  // than the last applied one may replace what the page shows.
  const sent = useRef(0);
  const applied = useRef(0);
  const current = useRef({ scope, load, known });
  current.current = { scope, load, known };

  useEffect(() => {
    generation.current += 1;
    const owner = generation.current;
    sent.current = 0;
    applied.current = 0;
    setFailure(null);
    if (!known) return;
    const order = ++sent.current;
    load()
      .then((value) => {
        if (owner !== generation.current || order <= applied.current) return;
        applied.current = order;
        setSnapshot({ scope, value });
        setOff(null);
      })
      .catch((error) => {
        if (owner !== generation.current || order <= applied.current) return;
        if (isOff(error)) {
          // Authoritative off: drop what was shown and fence every older read.
          applied.current = order;
          setSnapshot(null);
          setFailure(null);
          setOff({ scope });
        } else
          setFailure({ scope, message: agentErrorMessage(error, failureMessage, undefined, copy) });
      });
    return () => {
      generation.current += 1;
    };
  }, [scope, attempt]);

  /** Re-reads the server state; resolves false when the answer no longer applies. */
  const refresh = useCallback(async () => {
    const owner = generation.current;
    const { scope: requested, load: read, known: ready } = current.current;
    if (!ready) return false;
    const order = ++sent.current;
    let value: T;
    try {
      value = await read();
    } catch (error) {
      // A recognized off is recorded here too (then still thrown to the caller); any other
      // failure keeps what is shown.
      if (isOff(error) && owner === generation.current && order > applied.current) {
        applied.current = order;
        setSnapshot(null);
        setFailure(null);
        setOff({ scope: requested });
      }
      throw error;
    }
    if (owner !== generation.current || order <= applied.current) return false;
    applied.current = order;
    setSnapshot({ scope: requested, value });
    setFailure(null);
    setOff(null);
    return true;
  }, []);

  return {
    scope,
    data: snapshot?.scope === scope ? snapshot.value : null,
    error: failure?.scope === scope ? failure.message : '',
    /** The list failed because agent access is off (not a transient failure). */
    disabled: off?.scope === scope,
    /** Captures the current generation; compare with `isCurrent` after awaiting. */
    owner: () => generation.current,
    isCurrent: (owner: number) => owner === generation.current,
    refresh,
    retry: () => setAttempt((value) => value + 1),
  };
}

/** A clock that re-renders its owner every `ms` while `active`. */
export function useNow(ms: number, active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    let timer = 0;
    const tick = () => {
      setNow(Date.now());
      timer = window.setTimeout(tick, ms);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [ms, active]);
  return now;
}
